// import ContractActivityCreate from './ContractActivityCreate'
// import ContractActivityDelete from './ContractActivityDelete'
// import ContractActivityUpdate from './ContractActivityUpdate'
import SrDocumentActivityCreate from './SrDocumentActivityCreate'
import SrDocumentActivityDelete from './SrDocumentActivityDelete'
import SrDocumentActivityUpdate from './SrDocumentActivityUpdate'
import { fetchSrDocumentActivities } from 'actions/srDocuments/srDocumentActivityActions'
import EmptyList from 'components/common/EmptyList'
import Modal from 'components/common/Modal'
import Section from 'components/common/Section'
import Table from 'components/common/tables/Table'
import { useAuth } from 'hooks/useAuth'
import useModal from 'hooks/useModal'
import _ from 'lodash'
import { MDBIcon } from 'mdbreact'
import React, { useEffect, useState, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { isUserNotReadOnlyForSiteReadiness } from 'utils/authUtils'
import { fetchDocumentAndDownload } from 'utils/downloadUtils'

const newHeader = 'New Activity'
const editHeader = 'Edit Activity'
const deleteHeader = 'Delete Activity'
const newFlag = 'new'
const editFlag = 'edit'
const deleteFlag = 'delete'

const SrDocumentActivityList = ({ srDocument }) => {
  //// HOOKS.
  const dispatch = useDispatch()
  const { user } = useAuth() ?? {}
  const { user_level, is_admin } = user ?? {}
  const {
    modalOpen,
    headerText,
    setHeaderText,
    formId,
    footer,
    setFooter,
    actions,
    setActions,
    selected,
    setSelected,
    params,
    setParams,
    setModalState,
  } = useModal()

  //// GLOBAL STATE.
  const { activitiesAvailable, lastAction } = useSelector(
    (state) => state.srDocumentActivities
  )

  //// LOCAL STATE.
  const [tableData, setTableData] = useState({
    columns: [
      {
        label: 'Date Occurred (UTC)',
        field: 'activity_occurred_at',
        sort: 'desc',
        date: true,
        minimal: 'lg',
      },
      {
        label: 'Status',
        field: 'sr_document_status.name',
        sort: 'asc',
      },
      {
        label: 'Location',
        field: 'sr_document_location.name',
        sort: 'asc',
      },
      {
        label: 'Comment',
        field: 'comment',
        sort: 'asc',
      },
      {
        label: '',
        field: 'buttons',
        sort: 'disabled',
        minimal: 'lg',
      },
    ],
  })

  //// LIFECYCLE HELPERS.

  //// MEMOS.

  //// EFFECTS.
  useEffect(() => {
    dispatch(fetchSrDocumentActivities(srDocument.id))
  }, [dispatch, srDocument.id])

  //// RENDER VARS & HELPERS.
  const toggleModal = useCallback(
    (e, type, item) => {
      if (e) e.preventDefault()

      if (type === newFlag) {
        setHeaderText(newHeader)
        setFooter(true)
        setActions([
          {
            text: 'Submit',
            color: 'primary',
            additionalProps: {
              form: formId,
              type: 'submit',
            },
          },
        ])
      } else if (type === editFlag) {
        setHeaderText(editHeader)
        setFooter(true)
        setActions([
          {
            text: 'Update',
            color: 'primary',
            additionalProps: {
              form: formId,
              type: 'submit',
            },
          },
        ])
        setSelected(item)
      } else if (type === deleteFlag) {
        setHeaderText(deleteHeader)
        setFooter(true)
        setActions([
          {
            text: 'Delete',
            color: 'danger',
            additionalProps: {
              form: formId,
              type: 'submit',
            },
          },
        ])
        setSelected(item)
      }
      if (type) {
        setModalState(true)
      } else {
        setModalState(false)
      }
    },
    [formId, setActions, setFooter, setHeaderText, setModalState, setSelected]
  )

  const handleDocumentDownload = useCallback(
    (e, item) => {
      e.preventDefault()
      const documentToFetch = {
        sr_document_id: srDocument.id,
        sr_document_document_id: item.sr_document_document_id,
      }
      fetchDocumentAndDownload(documentToFetch)
    },
    [srDocument.id]
  )

  //// RENDER.
  return (
    <>
      <Section
        header="Site Readiness Document Activities"
        description={
          srDocument.current_state !== 0
            ? `Site readiness document activities cannot be updated or added when the site readiness document is ${srDocument.current_state_text.toLowerCase()}`
            : 'View, add, and edit site readiness document activity'
        }
        menu={
          isUserNotReadOnlyForSiteReadiness(user_level)
            ? {
                order: 2,
                show: srDocument.current_state === 0 ? true : false,
                linkItems: [
                  {
                    name: (
                      <>
                        <MDBIcon icon="fas fa-plus" /> Add Activity
                      </>
                    ),
                    // url: `/sr-documents/${srDocument.id}/new/activity`,
                    exact: true,
                    additionalProps: {
                      active: true,
                      onClick: (e) => toggleModal(e, newFlag),
                      className: 'btn-sm',
                    },
                  },
                ],
              }
            : null
        }
      >
        {_.size(activitiesAvailable) ? (
          <Table
            section="Site Readiness Activity Activity"
            tableData={tableData}
            setTableData={setTableData}
            rowData={activitiesAvailable}
            sort={['activity_occurred_at', 'desc']}
            toggleModal={toggleModal}
            downloadFile={handleDocumentDownload}
            updatedState={lastAction}
            downloadableCSV
            editButton={
              srDocument.current_state === 0 &&
              isUserNotReadOnlyForSiteReadiness(user_level)
            }
            deleteButton={is_admin}
            downloadButton
          />
        ) : _.isEmpty(activitiesAvailable) && srDocument.current_state === 0 ? (
          <EmptyList
            name="activity"
            namePlural="activities"
            userRole={user_level}
            createElement={
              <span className="link" onClick={(e) => toggleModal(e, newFlag)}>
                Create
              </span>
            }
          />
        ) : _.isEmpty(activitiesAvailable) ? (
          <p>
            No site readiness document activities were added to this site
            readiness document while open.
          </p>
        ) : (
          void 0
        )}
      </Section>
      <Modal
        isOpen={modalOpen}
        toggle={toggleModal}
        headerText={headerText}
        includeFooter={footer}
        actionButtons={actions}
      >
        {headerText === newHeader ? (
          <SrDocumentActivityCreate
            formId={formId}
            srDocumentId={srDocument.id}
            params={params}
            setParams={setParams}
            closeModal={() => setModalState(false)}
          />
        ) : headerText === editHeader ? (
          <SrDocumentActivityUpdate
            formId={formId}
            contractId={srDocument.id}
            params={params}
            setParams={setParams}
            selected={selected}
            setSelected={setSelected}
            closeModal={() => setModalState(false)}
          />
        ) : headerText === deleteHeader ? (
          <SrDocumentActivityDelete
            formId={formId}
            selected={selected}
            closeModal={() => setModalState(false)}
          />
        ) : (
          void 0
        )}
      </Modal>
    </>
  )
}

export default SrDocumentActivityList
